import { useRef, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { ProductForm } from '@features/products/components/ProductForm';
import { useCreateProduct } from '@features/products/hooks/useProducts';
import type { Product } from '@features/products/types/product.types';
import { createPortal } from 'react-dom';

interface ProductFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProductCreated?: (product: Product) => void;
}

export function ProductFormModal({ isOpen, onClose, onProductCreated }: ProductFormModalProps) {
  const createProductMutation = useCreateProduct();
  const panelRef = useRef<HTMLDivElement>(null);

  // Focus first input when modal opens
  useEffect(() => {
    if (isOpen && panelRef.current) {
      const timer = setTimeout(() => {
        const firstInput = panelRef.current?.querySelector('input, select, textarea') as HTMLElement;
        if (firstInput) {
          firstInput.focus();
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleSubmit = async (data: Partial<Product>) => {
    try {
      const newProduct = await createProductMutation.mutateAsync(data);
      if (onProductCreated) {
        onProductCreated(newProduct);
      }
      onClose();
    } catch (error) {
      console.error('Error creating product:', error);
    }
  };

  const handleClose = (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    // Only close if clicking directly on backdrop, not on children
    if (e.target === e.currentTarget) {
      // Don't close - we want explicit close button click
    }
  };

  if (!isOpen) return null;

  // Simple modal without Headless UI Dialog - avoids focus trap conflicts
  const modalContent = (
    <div
      className="fixed inset-0"
      style={{ zIndex: 99999 }}
      role="dialog"
      aria-modal="true"
      onClick={handleBackdropClick}
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 transition-opacity"
        style={{ zIndex: 99999 }}
      />

      {/* Modal container */}
      <div
        className="fixed inset-0 overflow-y-auto"
        style={{ zIndex: 100000 }}
      >
        <div className="flex min-h-full items-center justify-center p-4">
          {/* Modal panel */}
          <div
            ref={panelRef}
            className="w-full max-w-2xl transform rounded-2xl bg-white dark:bg-secondary-800 p-4 sm:p-6 text-left shadow-2xl transition-all animate-in fade-in zoom-in-95 duration-200"
            style={{ zIndex: 100001 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium leading-6 text-secondary-900 dark:text-white">
                Crear Nuevo Producto
              </h3>
              <button
                type="button"
                onClick={handleClose}
                className="rounded-lg p-1 text-secondary-400 hover:bg-secondary-100 dark:hover:bg-secondary-700 hover:text-secondary-600 dark:hover:text-secondary-200 transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Form */}
            <div className="mt-2">
              <ProductForm
                onSubmit={handleSubmit}
                onCancel={handleClose}
                isLoading={createProductMutation.isPending}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Render via portal to escape parent stacking context completely
  return createPortal(modalContent, document.body);
}
